import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import Typography from '@nubank/nuds-web/components/Typography/Typography';
import TextField from '@nubank/nuds-web/components/TextField/TextField';
import Checkbox from '@nubank/nuds-web/components/Checkbox/Checkbox';
import Button from '@nubank/nuds-web/components/Button/Button';
import Snackbar from '@nubank/nuds-web/components/Snackbar/Snackbar';
import { strictlyMergeMatchingProperties } from '@nubank/nuds-web/utils/object/mergeMatchingProperties';
import Link from '@nubank/nuds-web/components/Link/Link';

import RegistrationRealtimeLoader from 'patterns/RegistrationRealtimeLoader/RegistrationRealtimeLoader';
import RegistrationUnavailableLanguage from 'patterns/RegistrationUnavailableLanguage/RegistrationUnavailableLanguage';
import RegistrationStatus from 'patterns/RegistrationStatus/RegistrationStatus';
import NuDSFormBR from 'components/NuDSFormBR/NuDSFormBR';
import { getProspectType, MULTI_PRODUCT, ProspectTypeShape } from 'utils/prospectTypes';
import { DEFAULT_LOCALE } from 'utils/i18nUtils';
import registerProspect from 'domains/prospect/registerProspect';
import { trackFieldFilling, trackFormSubmission } from 'tracking/application/form';
import { sentryException, ERROR_SEVERITY } from 'services/errorTracking/sentryException';

import { FormContent } from './styles/FormContent';

const errorMessages = {
  name: 'Precisamos do seu nome completo.',
  cpf: 'Precisamos de um CPF válido.',
  email: 'Precisamos de um email válido.',
  emailConfirmation: 'Ops. Está diferente do campo acima.',
  accepted: 'Para prosseguir, você deve concordar com as políticas de privacidade.',
  generic: 'Ficamos fora do ar e não conseguimos completar seu pedido. Aguarde alguns minutos e tente novamente.',
};

const trackField = (valueKey, prospectType) => fieldName => event => {
  if (event.target[valueKey]) {
    trackFieldFilling({
      prospectType,
      field: fieldName,
    });
  }
};

function PersonProspectRegistrationForm({
  prospectType,
  initialValues,
  initialTouched,
  className,
  isOpenForm,
}) {
  const { buttonLabel, formTitle } = prospectType;
  const { locale } = useIntl();
  const [realtimeResult, setRealtimeResult] = useState(false);

  const createTextFieldTracker = useCallback(trackField('value', prospectType.type), [prospectType]);
  const createCheckboxTracker = useCallback(trackField('checked', prospectType.type), [prospectType]);

  const handleSubmit = async ({
    nextStep,
    setFormErrorMsg,
    setFormErrors,
    values,
    goToStep,
    setSubmitting,
  }) => {
    const { name, cpf, email } = values;
    const prospect = { name, cpf, email };
    const STEP_FEEDBACK_INDEX = 2;

    trackFormSubmission({ prospectType: prospectType.type });

    const displayEmailFeedback = () => {
      setSubmitting(false);
      goToStep(STEP_FEEDBACK_INDEX);
    };

    try {
      await registerProspect({
        prospect,
        prospectType,
        onThrottledResponse: displayEmailFeedback,
        onApplicationFinish: displayEmailFeedback,
        onAnalysisStart() {
          setSubmitting(false);
          nextStep();
        },
        onAnalysisFinish(analysisResult) {
          setRealtimeResult(analysisResult);
          goToStep(STEP_FEEDBACK_INDEX);
        },
      });
    } catch (errors) {
      setSubmitting(false);
      const { generic, ...fields } = errors;

      const fieldsErrors = strictlyMergeMatchingProperties(fields, errorMessages);
      const isAnyFieldWithError = Boolean(Object.keys(fieldsErrors).length);

      if (isAnyFieldWithError) {
        setFormErrors(fieldsErrors);
      }

      if (generic || !isAnyFieldWithError) {
        setFormErrorMsg(errorMessages.generic);
      }

      sentryException({
        error: errors,
        flow: 'application_flow',
        checkpoint: 'register_prospect',
        namespace: '<PersonProspectRegistrationForm/>',
        level: ERROR_SEVERITY.CRITICAL,
      });
    }
  };

  if (locale !== DEFAULT_LOCALE) {
    return <RegistrationUnavailableLanguage isOpenForm={isOpenForm} />;
  }

  return (
    <NuDSFormBR className={className}>
      <NuDSFormBR.Step
        initialValues={initialValues}
        initialTouched={initialTouched}
        onSubmit={handleSubmit}
      >
        {({
          isDisabled,
          formErrorMsg,
          clearFormErrorMsg,
          isSubmitting,
        }) => (
          <FormContent>
            <Typography
              variant="subtitle"
              tag="h3"
              color="black"
              colorVariant="default"
              gutterBottom={2}
              intlKey={formTitle}
            />

            <TextField
              id="field-name"
              type="name"
              name="name"
              label="Nome completo"
              onBlur={createTextFieldTracker('name')}
              syncValidations={{
                required: errorMessages.name,
                personName: errorMessages.name,
              }}
            />
            <TextField
              id="field-cpf"
              type="tel"
              name="cpf"
              label="CPF"
              onBlur={createTextFieldTracker('cpf')}
              syncValidations={{
                required: errorMessages.cpf,
                cpf: errorMessages.cpf,
              }}
            />
            <TextField
              id="field-email"
              type="email"
              name="email"
              label="E-mail"
              onBlur={createTextFieldTracker('email')}
              syncValidations={{
                required: errorMessages.email,
                email: errorMessages.email,
              }}
            />
            <TextField
              id="field-emailConfirmation"
              type="email"
              name="emailConfirmation"
              label="Confirmação de E-mail"
              autoComplete="off"
              onBlur={createTextFieldTracker('emailConfirmation')}
              syncValidations={{
                required: errorMessages.email,
                equalsTo: { errorMsg: errorMessages.emailConfirmation, params: { target: 'email' } },
                email: errorMessages.email,
              }}
            />
            <Checkbox
              label={(
                <Typography
                  variant="paragraph1"
                  tag="span"
                  color="black"
                  colorVariant="default"
                  intlKey="COMMON.APPLICATION.LABEL.PRIVACY_POLICY"
                  intlValues={{
                    a: content => (
                      <Link
                        href="/contrato/politica-privacidade"
                        color="primary"
                        target="_blank"
                      >
                        {content}
                      </Link>
                    ),
                  }}
                />
              )}
              id="field-accepted"
              name="accepted"
              syncValidations={{
                required: errorMessages.accepted,
              }}
              onChange={createCheckboxTracker('privacyNotice')}
            />
            <Button
              type="submit"
              variant="contained"
              styleVariant="primary"
              extended
              disabled={isDisabled || isSubmitting}
              intlKey={buttonLabel}
            />

            <Snackbar
              visible={Boolean(formErrorMsg)}
              onActionClick={clearFormErrorMsg}
              actionText="Fechar"
            >
              {formErrorMsg}
            </Snackbar>
          </FormContent>
        )}
      </NuDSFormBR.Step>
      <NuDSFormBR.Step isForm={false}>
        {({ formErrorMsg, clearFormErrorMsg }) => (
          <>
            <RegistrationRealtimeLoader isOpenForm={isOpenForm} />
            <Snackbar
              visible={Boolean(formErrorMsg)}
              onActionClick={clearFormErrorMsg}
              actionText="Fechar"
            >
              {formErrorMsg}
            </Snackbar>
          </>
        )}
      </NuDSFormBR.Step>
      <NuDSFormBR.Step isForm={false}>
        {({ formsValues }) => (
          <RegistrationStatus
            prospectEmail={formsValues.email}
            analysed={realtimeResult}
            isOpenForm={isOpenForm}
          />
        )}
      </NuDSFormBR.Step>
    </NuDSFormBR>
  );
}

PersonProspectRegistrationForm.defaultProps = {
  className: null,
  initialTouched: {},
  initialValues: {
    name: '',
    cpf: '',
    email: '',
    emailConfirmation: '',
    accepted: false,
  },
  isOpenForm: false,
  prospectType: getProspectType(MULTI_PRODUCT),
};

PersonProspectRegistrationForm.propTypes = {
  className: PropTypes.string,
  initialTouched: PropTypes.shape({
    accepted: PropTypes.bool,
    cpf: PropTypes.bool,
    email: PropTypes.bool,
    emailConfirmation: PropTypes.bool,
    name: PropTypes.bool,
  }),
  initialValues: PropTypes.shape({
    accepted: PropTypes.bool,
    cpf: PropTypes.string,
    email: PropTypes.string,
    emailConfirmation: PropTypes.string,
    name: PropTypes.string,
  }),
  isOpenForm: PropTypes.bool,
  prospectType: ProspectTypeShape,
};

export default PersonProspectRegistrationForm;
